<template>
  <div class="asview">
    <p class="asview__lead">
      최고관리자만 사용할 수 있습니다. 판매업체 아이디로 판매관리센터를 열면 그 업체가 보는 화면과 같습니다.
      저장·처리도 해당 업체로 반영됩니다. 관리자 로그인은 그대로 유지됩니다.
    </p>

    <section class="sui-card sui-card--flush" data-tour="admin-seller-view-list">
      <div class="sui-toolbar" data-tour="admin-seller-view-toolbar">
        <label class="sui-search">
          <AppIcon icon="mdi-magnify" :size="17" />
          <input
            v-model.trim="keyword"
            type="search"
            placeholder="아이디 · 상호 · 업체코드 검색"
          />
        </label>
        <span class="sui-toolbar__spacer" />
        <button type="button" class="sui-btn" :disabled="loading" @click="load">
          <AppIcon icon="mdi-refresh" :size="16" />
          새로고침
        </button>
      </div>

      <p v-if="errorMessage" class="asview__error">
        <AppIcon icon="mdi-alert-outline" :size="16" />
        {{ errorMessage }}
        <button type="button" class="sui-btn sui-btn--sm" @click="load">다시 시도</button>
      </p>

      <div v-if="filtered.length" class="sui-table-wrap">
        <table class="sui-table">
          <thead>
            <tr>
              <th>업체</th>
              <th>아이디</th>
              <th class="is-center">상태</th>
              <th class="is-center">보기</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in filtered" :key="row.sellerCode">
              <td>
                <span class="sui-table__strong">{{ row.storeName || row.sellerId }}</span>
                <span class="sui-table__sub">{{ row.sellerCode }} · {{ row.bizName || '—' }}</span>
              </td>
              <td>
                <span class="asview__id">{{ row.sellerId }}</span>
              </td>
              <td class="is-center">
                <span class="sui-badge" :class="statusTone(row.status)">
                  {{ SELLER_STATUS_LABEL[row.status] }}
                </span>
              </td>
              <td class="is-center">
                <button
                  type="button"
                  class="sui-btn sui-btn--primary sui-btn--sm"
                  data-tour="admin-seller-view-open"
                  :disabled="!canOpen(row) || openingCode === row.sellerCode"
                  @click="openAsSeller(row)"
                >
                  {{ openingCode === row.sellerCode ? '여는 중…' : '이 업체로 보기' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p v-else-if="!loading && !errorMessage" class="asview__empty">
        조건에 맞는 판매업체가 없습니다.
      </p>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import { fetchAdminSellers, impersonateAdminSeller, type AdminSellerRow } from '@/api/admin'
import { useSellerAuth } from '@/composables/useSellerAuth'
import { SELLER_STATUS_LABEL, type SellerStatus } from '@/constants/sellerSession'

const router = useRouter()
const { applySession } = useSellerAuth()

const sellers = ref<AdminSellerRow[]>([])
const loading = ref(false)
const errorMessage = ref('')
const keyword = ref('')
const openingCode = ref('')

const STATUS_TONE: Record<SellerStatus, string> = {
  PENDING: 'sui-badge--violet',
  APPROVED: 'sui-badge--green',
  REJECTED: 'sui-badge--red',
  SUSPENDED: 'sui-badge--amber',
  CLOSED: 'sui-badge--gray',
}

function statusTone(status: SellerStatus) {
  return STATUS_TONE[status] ?? 'sui-badge--gray'
}

function canOpen(row: AdminSellerRow) {
  return row.status === 'APPROVED' || row.status === 'PENDING'
}

const filtered = computed(() => {
  const q = keyword.value.toLowerCase()
  return sellers.value.filter((row) => {
    if (!q) {
      return true
    }
    const hay = [row.sellerId, row.storeName, row.bizName, row.sellerCode]
      .join(' ')
      .toLowerCase()
    return hay.includes(q)
  })
})

async function load() {
  loading.value = true
  errorMessage.value = ''
  try {
    sellers.value = await fetchAdminSellers()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '목록을 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

async function openAsSeller(row: AdminSellerRow) {
  if (!canOpen(row) || openingCode.value) {
    return
  }

  openingCode.value = row.sellerCode
  errorMessage.value = ''
  try {
    const seller = await impersonateAdminSeller(row.sellerCode)
    applySession(seller)
    const opened = window.open('/seller', '_blank')
    if (!opened) {
      await router.push({ name: 'seller-dashboard' })
    }
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '판매업체 화면을 열지 못했습니다.'
  } finally {
    openingCode.value = ''
  }
}

onMounted(load)
</script>

<style lang="scss" src="./AdminSellerViewPage.scss"></style>
